export type WeaponKind = 'sniper' | 'rifle' | 'pistol' | 'knife' | 'smg';

export type Rarity = 'consumer' | 'industrial' | 'milspec' | 'restricted' | 'classified' | 'covert' | 'contraband';

export type Wear = 'FN' | 'MW' | 'FT' | 'WW' | 'BS';

export type Bidder = {
  id: string;
  firstName: string;
  username: string | null;
  initial: string;
  hue: number;
  bidCount: number;
};

export type BidEntry = {
  ts: number;
  bidder: Bidder;
  price: number;
  bidIndex?: number;
};

export type Lot = {
  id: string;
  code: string;
  tier: 'A' | 'B' | 'C';
  weapon: WeaponKind;
  weaponLabel: string;
  skinName: string;
  rarity: Rarity;
  wear: Wear;
  float: number;
  marketPrice: number;
  currentPrice: number;
  bidsCount: number;
  timerEndsAt: number;
  startsAt?: number;
  closedAt?: number;
  winner?: Bidder;
  finalPrice?: number;
  lastBidder: Bidder;
  recentBidders: Bidder[];
  totalBidders: number;
  bidHistory?: BidEntry[];
  chatDeepLink?: string;
  palette: { from: string; via: string; to: string; accent: string };
};

export type RecentWin = {
  id: string;
  username: string;
  initial: string;
  hue: number;
  weapon: WeaponKind;
  weaponLabel: string;
  skinName: string;
  finalPrice: number;
  marketPrice: number;
  bidsCount: number;
  closedMinutesAgo: number;
  palette: { from: string; to: string };
};

const now = Date.now();
const inSec = (s: number) => now + s * 1000;

const bidders: readonly Bidder[] = [
  { id: 'u1', firstName: 'Alex', username: 'alex_cs', initial: 'A', hue: 195, bidCount: 23 },
  { id: 'u2', firstName: 'Дмитрий', username: 'dmitry_skins', initial: 'Д', hue: 320, bidCount: 8 },
  { id: 'u3', firstName: 'Burak', username: null, initial: 'B', hue: 30, bidCount: 14 },
  { id: 'u4', firstName: 'Lukas', username: 'lukas_pl', initial: 'L', hue: 145, bidCount: 47 },
  { id: 'u5', firstName: 'Yusuf', username: 'yusuf_tr', initial: 'Y', hue: 280, bidCount: 3 },
  { id: 'u6', firstName: 'Никита', username: 'nik_pwr', initial: 'Н', hue: 55, bidCount: 19 },
  { id: 'u7', firstName: 'João', username: 'joao_br', initial: 'J', hue: 110, bidCount: 31 },
  { id: 'u8', firstName: 'Kemal', username: null, initial: 'K', hue: 5, bidCount: 6 },
  { id: 'u9', firstName: 'Игорь', username: 'igor_aimer', initial: 'И', hue: 220, bidCount: 11 },
  { id: 'u10', firstName: 'Mateo', username: 'mateo_arg', initial: 'M', hue: 340, bidCount: 27 },
];

const b = (i: number): Bidder => {
  const item = bidders[i];
  if (!item) throw new Error(`mock bidder ${i} missing`);
  return item;
};

const lotA04History: BidEntry[] = (() => {
  const order = [3, 0, 5, 2, 7, 3, 9, 0, 6, 5, 4, 2, 1, 3] as const;
  return order.map((idx, i) => {
    const fromEnd = order.length - 1 - i;
    return {
      ts: now - fromEnd * 26_000 - Math.floor(Math.random() * 8000),
      bidder: b(idx),
      price: +(4.32 - fromEnd * 0.01).toFixed(2),
    };
  });
})();

export const liveLots: Lot[] = [
  {
    id: 'lot-a04',
    code: 'A-04',
    tier: 'A',
    weapon: 'sniper',
    weaponLabel: 'AWP',
    skinName: 'Asiimov',
    rarity: 'covert',
    wear: 'FT',
    float: 0.27,
    marketPrice: 142.5,
    currentPrice: 4.32,
    bidsCount: 432,
    timerEndsAt: inSec(47),
    lastBidder: b(3),
    recentBidders: [b(3), b(0), b(5), b(2), b(7)],
    totalBidders: 47,
    bidHistory: lotA04History,
    chatDeepLink: 'https://t.me/skinstep_chat/3',
    palette: { from: '#1a0d05', via: '#d97520', to: '#fff', accent: '#ff8c1a' },
  },
  {
    id: 'lot-a07',
    code: 'A-07',
    tier: 'A',
    weapon: 'rifle',
    weaponLabel: 'AK-47',
    skinName: 'Redline',
    rarity: 'classified',
    wear: 'MW',
    float: 0.11,
    marketPrice: 38.9,
    currentPrice: 1.18,
    bidsCount: 118,
    timerEndsAt: inSec(124),
    lastBidder: b(9),
    recentBidders: [b(9), b(6), b(1)],
    totalBidders: 19,
    palette: { from: '#0a0a0a', via: '#1f1f1f', to: '#c41e1e', accent: '#ff2d2d' },
  },
  {
    id: 'lot-a12',
    code: 'A-12',
    tier: 'A',
    weapon: 'pistol',
    weaponLabel: 'USP-S',
    skinName: 'Kill Confirmed',
    rarity: 'covert',
    wear: 'FN',
    float: 0.04,
    marketPrice: 92.4,
    currentPrice: 2.89,
    bidsCount: 289,
    timerEndsAt: inSec(8),
    lastBidder: b(4),
    recentBidders: [b(4), b(2), b(0)],
    totalBidders: 22,
    palette: { from: '#1c0606', via: '#3d0a0a', to: '#e6dfd5', accent: '#ff4444' },
  },
  {
    id: 'lot-b02',
    code: 'B-02',
    tier: 'B',
    weapon: 'knife',
    weaponLabel: 'Karambit',
    skinName: 'Doppler Phase 2',
    rarity: 'covert',
    wear: 'FN',
    float: 0.02,
    marketPrice: 612.0,
    currentPrice: 8.4,
    bidsCount: 840,
    timerEndsAt: inSec(32),
    lastBidder: b(8),
    recentBidders: [b(8), b(3), b(0), b(5)],
    totalBidders: 64,
    palette: { from: '#0a0014', via: '#3a0d6e', to: '#0fc6e8', accent: '#b537ff' },
  },
  {
    id: 'lot-a09',
    code: 'A-09',
    tier: 'A',
    weapon: 'pistol',
    weaponLabel: 'Glock-18',
    skinName: 'Fade',
    rarity: 'restricted',
    wear: 'FN',
    float: 0.01,
    marketPrice: 24.5,
    currentPrice: 0.66,
    bidsCount: 66,
    timerEndsAt: inSec(213),
    lastBidder: b(5),
    recentBidders: [b(5), b(7), b(1)],
    totalBidders: 12,
    palette: { from: '#2a0830', via: '#e8358e', to: '#ffd166', accent: '#ff6cb1' },
  },
];

export const startingSoonLots: (Lot & { startsAt: number })[] = [
  {
    id: 'lot-b08',
    code: 'B-08',
    tier: 'B',
    weapon: 'rifle',
    weaponLabel: 'M4A4',
    skinName: 'Howl',
    rarity: 'contraband',
    wear: 'MW',
    float: 0.08,
    marketPrice: 1840,
    currentPrice: 0,
    bidsCount: 0,
    timerEndsAt: inSec(900),
    startsAt: inSec(184),
    lastBidder: b(0),
    recentBidders: [],
    totalBidders: 0,
    palette: { from: '#1c0500', via: '#8a1a0a', to: '#ff8a3d', accent: '#ff5722' },
  },
  {
    id: 'lot-c01',
    code: 'C-01',
    tier: 'C',
    weapon: 'knife',
    weaponLabel: 'M9 Bayonet',
    skinName: 'Crimson Web',
    rarity: 'covert',
    wear: 'FT',
    float: 0.21,
    marketPrice: 1240,
    currentPrice: 0,
    bidsCount: 0,
    timerEndsAt: inSec(900),
    startsAt: inSec(412),
    lastBidder: b(0),
    recentBidders: [],
    totalBidders: 0,
    palette: { from: '#0a0000', via: '#1a0202', to: '#ff1a1a', accent: '#ff4444' },
  },
  {
    id: 'lot-a18',
    code: 'A-18',
    tier: 'A',
    weapon: 'pistol',
    weaponLabel: 'Desert Eagle',
    skinName: 'Blaze',
    rarity: 'restricted',
    wear: 'FN',
    float: 0.03,
    marketPrice: 42,
    currentPrice: 0,
    bidsCount: 0,
    timerEndsAt: inSec(900),
    startsAt: inSec(1180),
    lastBidder: b(0),
    recentBidders: [],
    totalBidders: 0,
    palette: { from: '#1a0a00', via: '#e8801a', to: '#ffd84d', accent: '#ff9d2e' },
  },
  {
    id: 'lot-b14',
    code: 'B-14',
    tier: 'B',
    weapon: 'smg',
    weaponLabel: 'MP9',
    skinName: 'Hot Rod',
    rarity: 'restricted',
    wear: 'FN',
    float: 0.02,
    marketPrice: 28,
    currentPrice: 0,
    bidsCount: 0,
    timerEndsAt: inSec(900),
    startsAt: inSec(2400),
    lastBidder: b(0),
    recentBidders: [],
    totalBidders: 0,
    palette: { from: '#1a0008', via: '#a00030', to: '#ff2d6e', accent: '#ff3da7' },
  },
];

const lotA02History: BidEntry[] = (() => {
  const order = [0, 2, 5, 0, 7, 3, 0, 9, 2, 5, 0, 6, 3, 0] as const;
  const final = 1.27;
  return order.map((idx, i) => {
    const fromEnd = order.length - 1 - i;
    return {
      ts: now - 240_000 - fromEnd * 18_000,
      bidder: b(idx),
      price: +(final - fromEnd * 0.01).toFixed(2),
    };
  });
})();

export const closedLots: Lot[] = [
  {
    id: 'lot-a02',
    code: 'A-02',
    tier: 'A',
    weapon: 'rifle',
    weaponLabel: 'M4A1-S',
    skinName: 'Hyper Beast',
    rarity: 'covert',
    wear: 'MW',
    float: 0.09,
    marketPrice: 78.4,
    currentPrice: 1.27,
    bidsCount: 127,
    timerEndsAt: now - 240_000,
    closedAt: now - 240_000,
    winner: b(0),
    finalPrice: 1.27,
    lastBidder: b(0),
    recentBidders: [b(0), b(6), b(3), b(9)],
    totalBidders: 28,
    bidHistory: lotA02History,
    palette: { from: '#001a0d', via: '#0a3a26', to: '#00c485', accent: '#00ff94' },
  },
];

export const recentWins: RecentWin[] = [
  {
    id: 'w1',
    username: 'lukas_pl',
    initial: 'L',
    hue: 145,
    weapon: 'sniper',
    weaponLabel: 'AWP',
    skinName: 'Asiimov',
    finalPrice: 3.18,
    marketPrice: 142.5,
    bidsCount: 318,
    closedMinutesAgo: 4,
    palette: { from: '#1a0d05', to: '#d97520' },
  },
  {
    id: 'w2',
    username: 'dmitry_skins',
    initial: 'Д',
    hue: 320,
    weapon: 'knife',
    weaponLabel: 'Butterfly',
    skinName: 'Fade',
    finalPrice: 9.21,
    marketPrice: 980,
    bidsCount: 921,
    closedMinutesAgo: 12,
    palette: { from: '#2a0830', to: '#e8358e' },
  },
  {
    id: 'w3',
    username: 'joao_br',
    initial: 'J',
    hue: 110,
    weapon: 'rifle',
    weaponLabel: 'M4A1-S',
    skinName: 'Hyper Beast',
    finalPrice: 1.45,
    marketPrice: 78,
    bidsCount: 145,
    closedMinutesAgo: 23,
    palette: { from: '#001a0d', to: '#00c485' },
  },
  {
    id: 'w4',
    username: 'igor_aimer',
    initial: 'И',
    hue: 220,
    weapon: 'sniper',
    weaponLabel: 'SSG 08',
    skinName: 'Blood in the Water',
    finalPrice: 0.84,
    marketPrice: 22,
    bidsCount: 84,
    closedMinutesAgo: 47,
    palette: { from: '#001428', to: '#0078e8' },
  },
];

export const tickerEvents = [
  { kind: 'bid', text: '@alex_cs bid LOT/A-04 · timer +10s' },
  { kind: 'win', text: '@lukas_pl claimed AWP | Asiimov for $3.18' },
  { kind: 'bid', text: '@yusuf_tr bid LOT/A-12 · timer +10s' },
  { kind: 'open', text: 'LOT/B-08 — M4A4 | Howl opens in 03:04' },
  { kind: 'bid', text: '@dmitry_skins bid LOT/B-02 · timer +10s' },
  { kind: 'win', text: '@joao_br claimed M4A1-S | Hyper Beast for $1.45' },
  { kind: 'bid', text: '@mateo_arg bid LOT/A-07 · timer +10s' },
  { kind: 'open', text: 'LOT/C-01 — M9 Crimson Web opens in 06:52' },
] as const;
